const SECTION_LOAD_DISTANCE = 800

/**
 * Keeps track of JS files that are currently
 * loading or have already been loaded.
 *
 * URL => Promise
 */
const scriptPromises = new Map()

/**
 * Keeps all module markers belonging to
 * each Shopify section.
 *
 * Shopify section element => Set(markers)
 */
const sectionModules = new WeakMap()

/**
 * Sections already being observed.
 */
const observedSections = new WeakSet()

let sectionObserver = null


/**
 * --------------------------------------------------------
 * Check whether section is already close enough
 * to the viewport that we should initialize it.
 * --------------------------------------------------------
 */
const isSectionNearViewport = (section) => {
  const rect = section.getBoundingClientRect()

  return (
    rect.top <= window.innerHeight + SECTION_LOAD_DISTANCE &&
    rect.bottom >= -SECTION_LOAD_DISTANCE
  )
}


/**
 * --------------------------------------------------------
 * Load an ESM JavaScript file once
 * --------------------------------------------------------
 *
 * IMPORTANT:
 *
 * Because esbuild uses:
 *
 * format: 'esm'
 * splitting: true
 *
 * these scripts MUST be loaded as type="module".
 */
const loadScript = (src) => {
  if (!src) {
    return Promise.reject(
      new Error('[Section JS] Missing script src')
    )
  }

  const absoluteSrc = new URL(
    src,
    document.baseURI
  ).href

  /**
   * Already loading / loaded.
   */
  if (scriptPromises.has(absoluteSrc)) {
    return scriptPromises.get(absoluteSrc)
  }

  /**
   * Check if script already exists on page.
   */
  const existingScript = Array.from(
    document.querySelectorAll('script[src]')
  ).find((script) => {
    return script.src === absoluteSrc
  })

  /**
   * Existing script could still be loading,
   * so don't immediately resolve.
   */
  if (existingScript) {
    const promise = new Promise(
      (resolve, reject) => {
        /**
         * Mark scripts loaded by this loader.
         */
        if (
          existingScript.dataset.sectionScriptLoaded === 'true'
        ) {
          resolve()
          return
        }

        existingScript.addEventListener(
          'load',
          resolve,
          {
            once: true
          }
        )

        existingScript.addEventListener(
          'error',
          () => {
            scriptPromises.delete(absoluteSrc)

            reject(
              new Error(
                `[Section JS] Failed to load ${absoluteSrc}`
              )
            )
          },
          {
            once: true
          }
        )
      }
    )

    scriptPromises.set(
      absoluteSrc,
      promise
    )

    return promise
  }

  const promise = new Promise(
    (resolve, reject) => {
      const script = document.createElement('script')

      script.type = 'module'
      script.src = absoluteSrc

      script.dataset.sectionScript = 'true'

      script.addEventListener(
        'load',
        () => {
          script.dataset.sectionScriptLoaded = 'true'

          resolve()
        },
        {
          once: true
        }
      )

      script.addEventListener(
        'error',
        () => {
          scriptPromises.delete(
            absoluteSrc
          )

          console.error(
            '[Section JS] Failed to load:',
            absoluteSrc
          )

          reject(
            new Error(
              `[Section JS] Failed to load ${absoluteSrc}`
            )
          )
        },
        {
          once: true
        }
      )

      document.head.appendChild(script)
    }
  )

  scriptPromises.set(
    absoluteSrc,
    promise
  )

  return promise
}


/**
 * --------------------------------------------------------
 * Find Shopify's actual section wrapper
 * --------------------------------------------------------
 *
 * Shopify outputs:
 *
 * #shopify-section-SECTION_ID
 */
const getSectionRoot = (marker) => {
  const sectionId = marker.dataset.sectionId

  if (!sectionId) {
    return null
  }

  return document.getElementById(
    `shopify-section-${sectionId}`
  )
}


/**
 * --------------------------------------------------------
 * Initialize one JS module for one section
 * --------------------------------------------------------
 */
const initializeModule = async (
  marker,
  section
) => {
  if (!marker || !section) {
    return
  }

  /**
   * Don't initialize same module marker twice.
   */
  if (
    marker.dataset.sectionJsInitialized === 'true'
  ) {
    return
  }

  const src =
    marker.dataset.sectionJsSrc

  const moduleName =
    marker.dataset.sectionModule

  if (!src || !moduleName) {
    return
  }

  /**
   * Mark as initializing immediately so multiple
   * observer callbacks can't initialize it twice.
   */
  marker.dataset.sectionJsInitialized = 'true'

  try {
    /**
     * Load JS file.
     *
     * If dropdown.js is needed by 10 sections,
     * this file is still downloaded only once.
     */
    await loadScript(src)

    /**
     * Each section JS file should register itself:
     *
     * window.ThemeSectionInitializers['dropdown']
     */
    const initializer =
      window.ThemeSectionInitializers?.[
        moduleName
      ]

    if (typeof initializer !== 'function') {
      console.warn(
        `[Section JS] Initializer "${moduleName}" was not found`
      )

      return
    }

    /**
     * Initialize this specific Shopify section.
     */
    initializer(section)

  } catch (error) {
    /**
     * Allow another attempt if loading failed.
     */
    marker.dataset.sectionJsInitialized = 'false'

    console.error(
      `[Section JS] Unable to initialize "${moduleName}"`,
      error
    )
  }
}


/**
 * --------------------------------------------------------
 * Initialize all JS modules belonging to a section
 * --------------------------------------------------------
 *
 * Example:
 *
 * connect-features.js
 * dropdown.js
 *
 * can both belong to the same Shopify section.
 */
const initializeSection = async (section) => {
  const markers =
    sectionModules.get(section)

  if (!markers || !markers.size) {
    return
  }

  await Promise.all(
    Array.from(markers).map((marker) => {
      return initializeModule(
        marker,
        section
      )
    })
  )
}


/**
 * --------------------------------------------------------
 * IntersectionObserver
 * --------------------------------------------------------
 */
const getSectionObserver = () => {
  if (sectionObserver) {
    return sectionObserver
  }

  sectionObserver =
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return
          }

          const section = entry.target

          initializeSection(section)

          sectionObserver.unobserve(section)
        })
      },
      {
        root: null,

        /**
         * Download JS before user actually reaches
         * the section.
         */
        rootMargin:
          `${SECTION_LOAD_DISTANCE}px 0px`,

        threshold: 0
      }
    )

  return sectionObserver
}


/**
 * --------------------------------------------------------
 * Register one marker
 * --------------------------------------------------------
 */
const registerMarker = (marker) => {
  if (
    marker.dataset.sectionJsRegistered === 'true'
  ) {
    return
  }

  const section =
    getSectionRoot(marker)

  if (!section) {
    console.warn(
      '[Section JS] Shopify section wrapper not found:',
      marker.dataset.sectionId
    )

    return
  }

  marker.dataset.sectionJsRegistered = 'true'

  /**
   * Store module against section.
   */
  let markers =
    sectionModules.get(section)

  if (!markers) {
    markers = new Set()

    sectionModules.set(
      section,
      markers
    )
  }

  markers.add(marker)

  const sectionIndex = Number(
    marker.dataset.sectionIndex || 0
  )

  /**
   * Immediately initialize:
   *
   * - section.index isn't available
   * - first 2 sections
   * - IntersectionObserver unsupported
   * - section is already close to viewport
   *
   * That last condition is particularly useful
   * when refreshing while halfway down the page.
   */
  if (
    sectionIndex === 0 ||
    sectionIndex <= 2 ||
    !('IntersectionObserver' in window) ||
    isSectionNearViewport(section)
  ) {
    initializeSection(section)

    return
  }

  /**
   * Only observe each Shopify section once,
   * even when it has several JS modules.
   */
  if (!observedSections.has(section)) {
    observedSections.add(section)

    getSectionObserver().observe(
      section
    )
  }
}


/**
 * --------------------------------------------------------
 * Find and register section JS markers
 * --------------------------------------------------------
 */
const initLazySectionJs = (
  scope = document
) => {
  let markers = []

  /**
   * Normal document / Shopify section wrapper.
   */
  if (scope.querySelectorAll) {
    markers = Array.from(
      scope.querySelectorAll(
        '[data-section-js]:not([data-section-js-registered])'
      )
    )
  }

  /**
   * Handle case where scope itself is a marker.
   */
  if (
    scope.matches?.(
      '[data-section-js]:not([data-section-js-registered])'
    )
  ) {
    markers.unshift(scope)
  }

  markers.forEach(
    registerMarker
  )
}


/**
 * --------------------------------------------------------
 * Initial page load
 * --------------------------------------------------------
 */
if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initLazySectionJs()
    }
  )
} else {
  initLazySectionJs()
}


/**
 * --------------------------------------------------------
 * Browser restored page / back-forward cache
 * --------------------------------------------------------
 */
window.addEventListener(
  'pageshow',
  () => {
    initLazySectionJs()
  }
)


/**
 * --------------------------------------------------------
 * Shopify Theme Editor
 * --------------------------------------------------------
 */
document.addEventListener(
  'shopify:section:load',
  (event) => {
    initLazySectionJs(
      event.target
    )
  }
)

export default function LazyLoad() {
  return {
    init: initLazySectionJs
  }
}